import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  shouldUseVerifiedDestinationAddress,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import ArrowUpRightSvg from "@redotech/redo-icons/arrow-up-right_filled.svg";
import TriangleDownSvg from "@redotech/redo-icons/triangle-down_filled.svg";
import TriangleRightSvg from "@redotech/redo-icons/triangle-right_filled.svg";
import { buildExternalOrderLink } from "@redotech/redo-merchant-app-common/build-external-order-link";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import {
  RedoHorizontalTab,
  RedoHorizontalTabs,
} from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useContext, useMemo, useState } from "react";
import {
  getShippingSelectionCellData,
  ShippingSelectionCell,
} from "../fulfillment-groups/cells/shipping-selection-cell";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { deliveryMethodText } from "./delivery-method-badge";
import { ItemsTable } from "./items-table";
import * as styles from "./merge-modal.module.css";
import { OrderFulfillmentAddress } from "./order-fulfillment-address";
import { ItemSection, SectionedItemsTable } from "./sectioned-items-table";
import { FulfillmentGroupStatusBadge } from "./status-badge";

export const MergeModal = memo(function MergeModal({
  groups,
  open,
  onCancel,
  onSkip,
  postMerge,
  postMergeLoad,
  cancelLoad,
  headerTitle,
  onModalClose,
  analyticsSource,
}: {
  groups: readonly FulfillmentGroupData[];
  open: boolean;
  onCancel: () => void;
  onSkip?: () => void;
  postMerge: (result: FulfillmentGroupData) => Promise<void>;
  postMergeLoad?: LoadState<void>;
  cancelLoad?: LoadState<void>;
  headerTitle: string;
  onModalClose: () => void;
  analyticsSource: "manual" | "suggestion" | "scan";
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);

  const [selectedGroup, setSelectedGroup] =
    useState<FulfillmentGroupData | null>(null);
  const [activeTab, setActiveTab] = useState<"orders" | "items">("orders");

  // Split groups into two balanced columns so item expansion doesn't look dumb
  const { leftColumn, rightColumn } = useMemo(() => {
    const midpoint = Math.ceil(groups.length / 2);
    return {
      leftColumn: groups.slice(0, midpoint),
      rightColumn: groups.slice(midpoint),
    };
  }, [groups]);

  const itemSections = useMemo(() => {
    return groups.map(
      (group): ItemSection => ({
        header: group.orders.map((order) => order.name).join(", "),
        items: group.items || [],
      }),
    );
  }, [groups]);

  const tabOptions: RedoHorizontalTab<"orders" | "items">[] = [
    { key: "orders", label: "Orders" },
    { key: "items", label: "Items" },
  ];

  const groupSelected = useMemo(
    () =>
      groups.some((group) => selectedGroup && group._id === selectedGroup._id),
    [selectedGroup, groups],
  );

  const [mergeLoad, doMerge] = useTriggerLoad(async (signal) => {
    if (!selectedGroup || !rpcClient) {
      return;
    }

    const eventProperties = {
      fulfillmentGroupCount: groups.length,
      source: analyticsSource,
    };
    amplitude.logEvent("oms-fulfillment-merge-submitted", eventProperties);
    try {
      const result = await rpcClient.mergeFulfillmentGroups(
        {
          groupIds: groups.map((group) => group._id),
          primaryGroupId: selectedGroup._id,
        },
        { signal },
      );

      amplitude.logEvent("oms-fulfillment-merge-succeeded", {
        ...eventProperties,
        mergedFulfillmentGroupId: result.group._id,
      });

      await postMerge(result.group);
    } catch (e) {
      amplitude.logEvent("oms-fulfillment-merge-failed", eventProperties);
      if ((e as Error).message.includes("Cannot merge fulfillment groups")) {
        toast((e as Error).message, { variant: "error" });
      } else {
        toast("Failed to merge shipments", { variant: "error" });
      }
      console.error((e as Error).message);
    }
  });

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={onModalClose}
      otherClasses={[styles.mergeModal]}
    >
      <RedoModalHeader
        cancelClicked={onModalClose}
        subtitle="Select a shipping address to use for this merged shipment."
        title={headerTitle}
      />
      <Flex dir="column" gap="none" grow={1} minH="none" w="full">
        <RedoHorizontalTabs
          drawBottomLine={false}
          pl="3xl"
          selectedTab={activeTab}
          setSelectedTab={setActiveTab}
          tabs={tabOptions}
        />
        <div className={styles.divider} />
        {activeTab === "orders" ? (
          <Flex
            className={styles.shipmentOptions}
            dir="row"
            gap="xl"
            grow={1}
            minH="none"
            px="3xl"
            py="3xl"
            w="full"
          >
            <Flex dir="column" gap="xl" w="full">
              {leftColumn.map((group) => (
                <MergeShipmentCard
                  group={group}
                  isSelected={selectedGroup?._id === group._id}
                  key={group._id}
                  setSelected={setSelectedGroup}
                />
              ))}
            </Flex>
            <Flex dir="column" gap="xl" w="full">
              {rightColumn.map((group) => (
                <MergeShipmentCard
                  group={group}
                  isSelected={selectedGroup?._id === group._id}
                  key={group._id}
                  setSelected={setSelectedGroup}
                />
              ))}
            </Flex>
          </Flex>
        ) : (
          <Flex
            className={styles.tableOptions}
            dir="column"
            grow={1}
            minH="none"
            w="full"
          >
            <SectionedItemsTable sections={itemSections} team={team} />
          </Flex>
        )}
      </Flex>

      <Flex
        borderColor="secondary"
        borderRadius="none"
        borderStyle="solid"
        borderTopWidth="1px"
        className={styles.stickyFooter}
        dir="row"
        gap="md"
        justify="space-between"
        p="3xl"
        w="full"
      >
        <Flex dir="row" gap="md">
          <RedoButton
            hierarchy="secondary"
            onClick={() => {
              onCancel();
            }}
            pending={cancelLoad?.pending}
            size="md"
            text="Do not merge"
            theme="normal"
          />
          {onSkip && (
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                onSkip();
              }}
              pending={cancelLoad?.pending}
              size="md"
              text="Skip"
              theme="normal"
            />
          )}
        </Flex>
        <RedoButton
          disabled={!groupSelected || cancelLoad?.pending}
          hierarchy="primary"
          onClick={() => {
            void doMerge();
          }}
          pending={mergeLoad.pending || postMergeLoad?.pending}
          size="md"
          text="Merge shipments"
          theme="normal"
        />
      </Flex>
    </RedoBaseModal>
  );
});

const MergeShipmentCard = memo(function MergeShipmentCard({
  isSelected,
  setSelected,
  group,
}: {
  isSelected: boolean;
  setSelected: (selected: FulfillmentGroupData | null) => void;
  group: FulfillmentGroupData | null;
}) {
  const team = useContext(TeamContext);
  const [isItemsExpanded, setIsItemsExpanded] = useState(false);

  if (!group || !team) {
    return null;
  }

  const order = group.orders[0];
  const provider = group.providers?.[0];
  const externalOrderLink =
    order?.id && provider
      ? buildExternalOrderLink(provider, order.id, team.storeUrl)
      : undefined;

  const displayAddress = shouldUseVerifiedDestinationAddress(
    group,
    team.settings?.outboundLabels?.address?.autoCleanAddresses,
  )
    ? group.verifiedDestinationAddress
    : group.destinationAddress;

  return (
    <Flex
      className={classNames(styles.optionCard, isSelected && styles.selected)}
      flexDirection="column"
      gap="xl"
      onClick={() => setSelected(group)}
      p="2xl"
    >
      <Flex flexDirection="row" justify="space-between">
        <Flex dir="row">
          <RedoRadioButton
            onSelect={() => setSelected(group)}
            selected={isSelected}
          />
          {group.orders.map(({ name }) => (
            <Text
              fontSize="sm"
              fontWeight={isSelected ? "medium" : "regular"}
              key={name}
            >
              {name}
            </Text>
          ))}
        </Flex>
        <FulfillmentGroupStatusBadge fulfillmentGroupStatus={group.status} />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Customer name
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {group.destinationAddress.name}
        </Text>
      </Flex>
      <Flex gap="5xl">
        <Flex grow={1}>
          <OrderFulfillmentAddress
            displayAddress={displayAddress}
            fontWeight="medium"
            hideEditButton
            hideName
          />
        </Flex>
        <Flex grow={1}>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Ship from
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {group.originAddress.name}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Service level
        </Text>
        <Text as="div" fontSize="xs" fontWeight="medium" textColor="secondary">
          {Array.from(new Set(group.shippingMethodNames)).map((name) => (
            <Text
              fontSize="xs"
              fontWeight="medium"
              key={name}
              textColor="secondary"
            >
              {name}
            </Text>
          ))}
        </Text>
      </Flex>
      <Flex flexDirection="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Delivery method
        </Text>
        <Flex dir="row" gap="xs">
          <ShippingInfo group={group} />
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="lg">
        <Flex
          align="center"
          className={styles.itemsToggle}
          dir="row"
          gap="xs"
          justify="space-between"
          onClick={(e) => {
            e.stopPropagation();
            setIsItemsExpanded(!isItemsExpanded);
          }}
        >
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Order items
          </Text>
          <Icon
            arbiterIconSvg={
              isItemsExpanded ? TriangleDownSvg : TriangleRightSvg
            }
            color="ghost"
            style={{ width: 16, height: 16 }}
          />
        </Flex>
        {group.items && group.items.length > 0 && isItemsExpanded && (
          <ItemsTable
            disableTooltip
            hideHeader
            items={group.items}
            team={team}
          />
        )}
      </Flex>
      {externalOrderLink && (
        <Flex>
          <RedoButton
            hierarchy="secondary"
            IconTrailing={ArrowUpRightSvg}
            onClick={() => {
              window.open(externalOrderLink, "_blank");
            }}
            text="View order"
          />
        </Flex>
      )}
    </Flex>
  );
});

const ShippingInfo = memo(function ShippingInfo({
  group,
}: {
  group: FulfillmentGroupData;
}) {
  if (group.deliveryMethod !== RedoShopifyDeliveryMethodType.Shipping) {
    return (
      <Text fontSize="xs" fontWeight="medium" textColor="primary">
        {group.deliveryMethod ? deliveryMethodText[group.deliveryMethod] : ""}
      </Text>
    );
  }

  const data = getShippingSelectionCellData(group);

  return (
    <ShippingSelectionCell
      data={data}
      deliveryMethod={group.deliveryMethod}
      hideLogo
      status={group.status}
    />
  );
});
